/**
 * Seller intake monitoring admin page (M20).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import RequirePermission from "../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { SELLER_SUBMISSION_ADMIN_PATH } from "../../../constants/sellerSubmissionAdminConstants";
import { fetchSellerIntakeMonitoringSummary } from "../../../services/sellerSubmissionAdminApi";

const RANGE_OPTIONS = [
  { value: "today", label: "Hoy" },
  { value: "7d", label: "Últimos 7 días" },
  { value: "30d", label: "Últimos 30 días" },
];

function MetricCard({ label, value, hint, to }) {
  const content = (
    <div className="crm-monitor-card">
      <div className="crm-monitor-card__label">{label}</div>
      <div className="crm-monitor-card__value">{value ?? "—"}</div>
      {hint ? <div className="crm-monitor-card__hint">{hint}</div> : null}
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="crm-monitor-card-link">
        {content}
      </Link>
    );
  }
  return content;
}

function BreakdownTable({ title, rows, labelKey = "key", countKey = "count" }) {
  if (!rows?.length) return null;
  return (
    <div className="crm-monitor-panel">
      <h3>{title}</h3>
      <table className="crm-monitor-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Conteo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row[labelKey]}-${row[countKey]}`}>
              <td>{row[labelKey]}</td>
              <td>{row[countKey]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminSellerIntakeMonitoringPageContent() {
  const [range, setRange] = useState("7d");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await fetchSellerIntakeMonitoringSummary({ range });
      setSummary(body.summary);
    } catch (err) {
      setError(err.message || "No pudimos cargar el monitoreo.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = useMemo(() => {
    if (!summary) return [];
    const { funnel, volume, backlog, duplicateSpam, photos, conversion, communication, shortcuts } =
      summary;
    return [
      {
        label: "Visitas",
        value: funnel?.pageViews,
        hint: "Analytics — página vista",
      },
      {
        label: "Inicios de formulario",
        value: funnel?.formStarted,
        hint: "Analytics",
      },
      {
        label: "Envíos almacenados",
        value: volume?.total,
        hint: "Solicitudes guardadas en rango",
        to: SELLER_SUBMISSION_ADMIN_PATH,
      },
      {
        label: "Tasa de éxito",
        value:
          funnel?.rates?.submitSuccessRate != null ? `${funnel.rates.submitSuccessRate}%` : "—",
        hint: "Analytics — éxito / intentos",
      },
      {
        label: "Errores de validación",
        value: funnel?.validationErrors,
        hint: "Analytics — eventos de validación",
      },
      {
        label: "Duplicados / spam",
        value: (duplicateSpam?.analyticsDuplicates || 0) + (duplicateSpam?.analyticsSpamRejected || 0),
        hint: "Analytics — guardrails",
      },
      {
        label: "Nuevas sin revisar",
        value: backlog?.newNotReviewed,
        hint: "Backlog operativo",
        to: shortcuts?.newSubmissions,
      },
      {
        label: "Seguimientos vencidos",
        value: backlog?.overdueFollowUps,
        hint: "Requieren acción",
        to: shortcuts?.overdueFollowUp,
      },
      {
        label: "Aprobadas sin convertir",
        value: backlog?.approvedNotConverted,
        hint: "Listas para borrador",
        to: shortcuts?.approvedNotConverted,
      },
      {
        label: "Fotos pendientes",
        value: photos?.submissionsWithPendingImages,
        hint: "Revisiones de imagen",
      },
      {
        label: "Borradores convertidos",
        value: conversion?.convertedDraftListings,
        hint: "sourceType seller_intake",
        to: shortcuts?.convertedDrafts,
      },
      {
        label: "Listos para publicar",
        value: conversion?.readyToPublishDrafts,
        hint: "Borradores seller_intake",
        to: shortcuts?.readyToPublishDrafts,
      },
      {
        label: "Sin contacto (+7 días)",
        value: backlog?.notContactedAfter7Days,
        hint: "Operaciones",
        to: shortcuts?.notContacted,
      },
    ];
  }, [summary]);

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Vendedores"
        title="Monitoreo — Vender mi carro"
        description="Métricas agregadas del flujo de envío de vendedores. Sin datos personales."
      />
      <AdminAutomotiveNav active="seller-submissions" />

      <main className="admin-console__main wrap">
        <div className="dealer-inventory-toolbar" style={{ marginBottom: "1rem" }}>
          <Link className="dealer-portal-btn" to={SELLER_SUBMISSION_ADMIN_PATH}>
            ← Bandeja de solicitudes
          </Link>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`dealer-portal-btn${range === option.value ? " dealer-portal-btn--primary" : ""}`}
                onClick={() => setRange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? <AdminStatePanel variant="loading" message="Cargando monitoreo…" /> : null}
        {error ? <AdminStatePanel variant="error" message={error} /> : null}

        {!loading && !error && summary ? (
          <>
            <div className="crm-monitor__cards">
              {cards.map((card) => (
                <MetricCard key={card.label} {...card} />
              ))}
            </div>

            <BreakdownTable title="Envíos por tipo de vendedor" rows={summary.volume?.bySellerType} />
            <BreakdownTable title="Estado de revisión (rango)" rows={summary.workflow?.byReviewStatus} />
            <BreakdownTable title="Estado de contacto (actual)" rows={summary.communication?.byContactStatus} />
            <BreakdownTable title="Imágenes por estado de revisión" rows={summary.photos?.imagesByReviewStatus} />
            <BreakdownTable title="Confiabilidad — resultStatus" rows={summary.reliability?.byResultStatus} />

            <p className="admin-table-subtext">
              Funnel completo vía analytics (M9). Los envíos almacenados provienen de{" "}
              <code>seller_vehicle_submissions</code>. Los borradores usan{" "}
              <code>sourceType = seller_intake</code>.
            </p>
          </>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminSellerIntakeMonitoringPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminSellerIntakeMonitoringPageContent />
    </RequirePermission>
  );
}
